import { StatusTone, TONE_BADGE } from './statusColors';

/**
 * O SELO DE CADA STATUS DA NOTA FISCAL, o domínio inteiro (decisão S1 do Heitor, 03/11/2026).
 * Prévia: https://claude.ai/artifact/U2Axig72sHh2B1XbZko4wq
 *
 * Até aqui a tela seguia a regra de 22/10 (E1): Autorizada neutra, cor só no que pede ação. Como no banco só existem
 * Autorizada e Cancelada, a tela inteira era cinza, e a nota boa e a cancelada eram o mesmo selo. Agora é o mapa da
 * fatura (Paga verde): o desfecho bom tem cor, o que pede ação de quem opera é âmbar, o que está em curso é azul.
 *
 * Os seis status do servidor (enum `FiscalDocumentStatus`). Status que não estiver aqui cai no neutro, e o teste
 * trava que os seis estão mapeados.
 */
export const STATUS_DA_NOTA = ['Rascunho', 'Processando', 'Autorizada', 'Rejeitada', 'Erro', 'Cancelada'] as const;
export type StatusDaNota = (typeof STATUS_DA_NOTA)[number];

/** `contorno` = encerrada sem ação: neutro de contorno, para não se confundir com a autorizada. */
export type TomDaNota = StatusTone | 'contorno';

export const TOM_DO_STATUS_DA_NOTA: Record<StatusDaNota, TomDaNota> = {
  Rascunho: 'warning',
  Processando: 'progress',
  Autorizada: 'success',
  Rejeitada: 'danger',
  Erro: 'danger',
  Cancelada: 'contorno',
};

const CONTORNO = 'bg-transparent text-slate-400 ring-1 ring-inset ring-slate-600';

export function tomDoStatusDaNota(status: string | null | undefined): TomDaNota {
  return (TOM_DO_STATUS_DA_NOTA as Record<string, TomDaNota>)[status || ''] ?? 'neutral';
}

/** As classes do selo: o mapa de tons do HUB, e o contorno da cancelada. */
export function seloDoStatusDaNota(status: string | null | undefined): string {
  const tom = tomDoStatusDaNota(status);
  return tom === 'contorno' ? CONTORNO : TONE_BADGE[tom];
}

/**
 * Os recortes de status da lista (decisão K1): os seis status e os dois GRUPOS que os cartões abrem. O cartão
 * Pendentes conta Rascunho e Processando e o Erros conta Erro e Rejeitada; filtrar só um dos dois mostrava uma lista
 * menor que o número do próprio cartão.
 */
export const GRUPOS_DE_STATUS = {
  pendentes: { rotulo: 'Pendentes (rascunho e processando)', status: ['Rascunho', 'Processando'] },
  erros: { rotulo: 'Erros (erro e rejeitada)', status: ['Erro', 'Rejeitada'] },
} as const;
export type GrupoDeStatus = keyof typeof GRUPOS_DE_STATUS;

/** A nota passa no recorte de status? Vazio = todas. */
export function passaNoStatus(status: string | null | undefined, recorte: string): boolean {
  if (!recorte) return true;
  const grupo = (GRUPOS_DE_STATUS as Record<string, { status: readonly string[] }>)[recorte];
  return grupo ? grupo.status.includes(String(status || '')) : status === recorte;
}

// ─── Ambiente da NFS-e (decisão A2) ─────────────────────────────────────────

/**
 * Onde a NFS-e mora: `nacional` (NFS-e Nacional, desde 01/11/2026) ou `municipal` (prefeitura, nulo no banco).
 * NFCom não tem ambiente, e rascunho com nulo também não (ainda não foi transmitido). O servidor já consulta, baixa o PDF e cancela por esse campo (`ambienteDoDocumento`); a tela
 * passa a dizer qual é, nas duas formas, para ninguém precisar saber a regra do nulo.
 */
export type AmbienteDaNota = 'nacional' | 'municipal';

export function ambienteDaNota(
  doc: { type?: string | null; status?: string | null; nfseAmbiente?: string | null } | null | undefined,
): AmbienteDaNota | null {
  if (!doc || doc.type !== 'nfse') return null;
  if (doc.nfseAmbiente === 'nacional') return 'nacional';
  // O servidor só grava o ambiente no transmit: rascunho nulo ainda não tem ambiente, e chamá-lo de municipal
  // mentiria sobre onde ele vai sair. O nulo só quer dizer municipal em nota que já passou pelo transmit.
  if (!doc.nfseAmbiente && doc.status === 'Rascunho') return null;
  return 'municipal';
}

/** O recorte de tipo da lista: NFS-e (as duas), NFS-e de um ambiente, ou NFCom. Vazio = todas. */
export const TIPOS_DA_LISTA = [
  { valor: 'nfse', rotulo: 'NFS-e' },
  { valor: 'nfse-nacional', rotulo: 'NFS-e nacional' },
  { valor: 'nfse-municipal', rotulo: 'NFS-e municipal' },
  { valor: 'nfcom', rotulo: 'NFCom' },
] as const;

export function passaNoTipo(doc: { type?: string | null; status?: string | null; nfseAmbiente?: string | null }, recorte: string): boolean {
  if (!recorte) return true;
  if (recorte === 'nfse-nacional') return ambienteDaNota(doc) === 'nacional';
  if (recorte === 'nfse-municipal') return ambienteDaNota(doc) === 'municipal';
  return doc.type === recorte;
}

/** O tipo que o servidor entende, para a exportação (o ambiente não tem filtro lá). */
export const tipoNoServidor = (recorte: string): string => (recorte.startsWith('nfse') ? 'nfse' : recorte);

/** O fim da chave, para achar a nota sem abrir o detalhe: "·· 680011". */
export function fimDaChave(chave: string | null | undefined, n = 6): string | null {
  const c = String(chave || '').replace(/\s/g, '');
  return c.length > n ? `·· ${c.slice(-n)}` : null;
}
